/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { JSX } from 'preact';

export const placeholderTypes = ['topPlaceholder', 'bottomPlaceholder'] as const;
/**
 * Type of placeholder (top which is before the data content and bottom which is after the data content)
 */
export type PlaceholderType = (typeof placeholderTypes)[number];

/**
 * Context object which is pass to the placeholderHeight function callback
 */
export type PlaceholderContext = {
  fromIndex: number;
  toIndex: number;
  estimateItemSize: number;
  which: PlaceholderType;
};

export const PLACEHOLDER_STYLE_CLASS = 'oj-collection-placeholder';

const DEFAULT_PLACEHOLDER_SIZE = 48; // min-height defined in Redwood spec for item
const TOP_PLACEHOLDER_KEY = '_topPlaceholder';
const BOTTOM_PLACEHOLDER_KEY = '_bottomPlaceholder';

type Props = {
  context: PlaceholderContext;
  placeholderHeight?: (context: PlaceholderContext) => number;
};

/**
 * A component that renders placeholder which is used internally by VirtualizeViewportCollection
 */
export const Placeholder = ({ context, placeholderHeight }: Props) => {
  const { fromIndex, toIndex, which, estimateItemSize } = context;
  let totalHeight = 0;
  if (placeholderHeight) {
    totalHeight = placeholderHeight(context);
  } else {
    const placeholderUnitHeight =
      estimateItemSize === 0 ? DEFAULT_PLACEHOLDER_SIZE : estimateItemSize;
    totalHeight = Math.max(0, toIndex - fromIndex) * placeholderUnitHeight;
  }

  const key = which === 'topPlaceholder' ? TOP_PLACEHOLDER_KEY : BOTTOM_PLACEHOLDER_KEY;
  const style: JSX.CSSProperties = { height: totalHeight + 'px' };
  if (totalHeight === 0) {
    style.display = 'none';
  }
  return <div key={key} className={PLACEHOLDER_STYLE_CLASS} style={style}></div>;
};
